import { useState, useRef } from 'react'
import {
	CalendarBlank,
	CaretDown,
	Flag,
	Tag,
	UserCircle,
	Buildings,
	Ruler,
	CircleDashed,
	Circle,
	CheckCircle,
	XCircle,
	Archive,
	ArrowsClockwise,
	X,
	Hash,
	ArrowsLeftRight,
} from '@phosphor-icons/react'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { Calendar } from '@journ/ui/calendar'
import { ComboboxDropdown } from '@journ/ui/combobox-dropdown'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@journ/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Separator } from '@journ/ui/separator'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import type { WorkItem, WorkItemPriority, WorkItemSize, WorkItemStatus, WorkItemWorkState } from '../../domain/workflow'
import { labelDraft$ } from '../../work-item-detail-store'
import { statusLabels } from '../work-item-detail-labels'
import { PriorityIndicator } from '../work-item-card-visuals'
import { useCustomerLookup } from '../../hooks/use-customer-lookup'
import { useAllWorkflows } from '../../hooks/use-all-workflows'
import { useReassignWorkflow } from '../../hooks/use-reassign-workflow'
import { useWorkItemDetailContext } from './work-item-detail-context'

const PRIORITIES: WorkItemPriority[] = ['P0', 'P1', 'P2', 'P3', 'P4']
const SIZES: WorkItemSize[] = ['XS', 'S', 'M', 'L', 'XL']

const rowClass =
	'group flex items-center gap-2.5 h-7 w-full rounded px-1.5 -mx-1.5 hover:bg-muted/50 transition-colors cursor-pointer text-left'
const iconClass = 'size-3.5 shrink-0 text-606060'
const valueClass = 'text-[13px] flex-1 min-w-0 truncate'
const emptyClass = 'text-[13px] flex-1 text-606060/70'
const caretClass = 'size-3 text-606060 opacity-0 group-hover:opacity-60 transition-opacity shrink-0'

const statusIcon = ({ status, workState }: { status: WorkItemStatus; workState: WorkItemWorkState }) => {
	if (status === 'inbox') return <CircleDashed className='size-3.5 text-606060 shrink-0' />
	if (status === 'open')
		return workState === 'in_progress' ? (
			<ArrowsClockwise className='size-3.5 text-green-500 shrink-0' />
		) : (
			<Circle className='size-3.5 text-blue-500 shrink-0' weight='fill' />
		)
	if (status === 'awaiting_handoff') return <Circle className='size-3.5 text-amber-500 shrink-0' weight='fill' />
	if (status === 'completed') return <CheckCircle className='size-3.5 text-green-500 shrink-0' />
	if (status === 'cancelled') return <XCircle className='size-3.5 text-red-400 shrink-0' />
	if (status === 'archived') return <Archive className='size-3.5 text-606060/50 shrink-0' />
	return <CircleDashed className='size-3.5 text-606060 shrink-0' />
}

const statusTextMuted = (status: WorkItemStatus): boolean =>
	status === 'completed' || status === 'cancelled' || status === 'archived'

type StatusFieldProps = {
	item: Pick<WorkItem, 'status' | 'workState'>
	statusValue: string
}

const StatusField = ({ item, statusValue }: StatusFieldProps) => (
	<div className={rowClass}>
		{statusIcon({ status: item.status, workState: item.workState })}
		<span className={`${valueClass} ${statusTextMuted(item.status) ? 'text-606060' : ''}`}>{statusValue}</span>
	</div>
)

const PriorityField = () => {
	const { detail } = useWorkItemDetailContext()
	const c = useIntlayer('work-item').detail
	const priority = detail.item?.priority ?? null

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button type='button' className={rowClass}>
					{priority ? <PriorityIndicator priority={priority} /> : <Flag className={iconClass} />}
					{priority ? (
						<span className={valueClass}>{priority}</span>
					) : (
						<span className={emptyClass}>{c.priorityNone}</span>
					)}
					<CaretDown className={caretClass} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align='start' className='w-36'>
				{priority !== null ? (
					<DropdownMenuItem onSelect={() => detail.setPriority(null)}>
						<span className='text-606060'>{c.priorityNone}</span>
					</DropdownMenuItem>
				) : null}
				{PRIORITIES.map(p => (
					<DropdownMenuItem key={p} onSelect={() => detail.setPriority(p)} className='flex items-center gap-2'>
						<PriorityIndicator priority={p} />
						<span>{p}</span>
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

const SizeField = () => {
	const { detail } = useWorkItemDetailContext()
	const c = useIntlayer('work-item').detail
	const size = detail.item?.size ?? null

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button type='button' className={rowClass}>
					<Ruler className={iconClass} />
					{size ? (
						<span className={valueClass}>{size}</span>
					) : (
						<span className={emptyClass}>{c.sizeNone}</span>
					)}
					<CaretDown className={caretClass} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align='start' className='w-28'>
				{size !== null ? (
					<DropdownMenuItem onSelect={() => detail.setSize(null)}>
						<span className='text-606060'>{c.sizeNone}</span>
					</DropdownMenuItem>
				) : null}
				{SIZES.map(s => (
					<DropdownMenuItem key={s} onSelect={() => detail.setSize(s)}>
						{s}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

const AssigneeField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item, actorCan, memberOptions, memberName, selectedAssignee, onAssign } = detail
	const c = useIntlayer('work-item').detail
	const [open, setOpen] = useState(false)
	const assigneeName = memberName(item?.assignedTo ?? null)
	const hasAssignee = Boolean(item?.assignedTo)

	if (!actorCan.assign) {
		return (
			<div className={rowClass}>
				<UserCircle className={iconClass} />
				<span className={hasAssignee ? valueClass : emptyClass}>{assigneeName}</span>
			</div>
		)
	}

	return (
		<Popover open={open} onOpenChange={setOpen} modal>
			<PopoverTrigger asChild>
				<button type='button' className={rowClass}>
					<UserCircle className={iconClass} />
					{hasAssignee ? (
						<span className={valueClass}>{assigneeName}</span>
					) : (
						<span className={emptyClass}>{c.assignPlaceholder.value}</span>
					)}
					<CaretDown className={caretClass} />
				</button>
			</PopoverTrigger>
			<PopoverContent className='p-0 w-56' align='start'>
				<ComboboxDropdown
					headless
					items={memberOptions}
					selectedItem={selectedAssignee}
					searchPlaceholder={c.assignPlaceholder.value}
					onSelect={opt => { onAssign(opt); setOpen(false) }}
				/>
			</PopoverContent>
		</Popover>
	)
}

const DueDateField = () => {
	const { detail } = useWorkItemDetailContext()
	const c = useIntlayer('work-item').detail
	const { formatPreferenceDate } = useDateFormat()
	const [open, setOpen] = useState(false)
	const dueDate = detail.item?.dueDate ?? null
	const selected = dueDate ? new Date(dueDate) : undefined

	const onSelect = (date: Date | undefined) => {
		detail.setDueDate(date ? date.toISOString().slice(0, 10) : null)
		setOpen(false)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button type='button' className={rowClass}>
					<CalendarBlank className={iconClass} />
					{dueDate ? (
						<span className={valueClass}>{formatPreferenceDate(dueDate)}</span>
					) : (
						<span className={emptyClass}>{c.noDueDate}</span>
					)}
					{dueDate ? (
						<button
							type='button'
							className='ml-auto text-606060 hover:text-foreground opacity-0 group-hover:opacity-60 transition-opacity'
							onClick={e => {
								e.stopPropagation()
								detail.setDueDate(null)
							}}
							aria-label={c.clearDueDate.value}
						>
							<X className='size-3' />
						</button>
					) : null}
				</button>
			</PopoverTrigger>
			<PopoverContent className='w-auto p-0' align='start'>
				<Calendar mode='single' selected={selected} onSelect={onSelect} initialFocus />
			</PopoverContent>
		</Popover>
	)
}

const CustomerField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item, setCustomer } = detail
	const c = useIntlayer('work-item').detail
	const [open, setOpen] = useState(false)
	const { customers } = useCustomerLookup(true)

	const customerOptions = customers.map(cust => ({ id: cust.id, label: cust.name }))
	const selectedCustomer = item?.customerId
		? { id: item.customerId, label: item.customerName ?? item.customerId }
		: undefined
	const hasCustomer = Boolean(item?.customerId)

	return (
		<Popover open={open} onOpenChange={setOpen} modal>
			<PopoverTrigger asChild>
				<button type='button' className={rowClass}>
					<Buildings className={iconClass} />
					{hasCustomer ? (
						<span className={valueClass}>{item?.customerName ?? item?.customerId}</span>
					) : (
						<span className={emptyClass}>{c.customerNone}</span>
					)}
					<CaretDown className={caretClass} />
				</button>
			</PopoverTrigger>
			<PopoverContent className='p-0 w-56' align='start'>
				<ComboboxDropdown
					headless
					items={customerOptions}
					selectedItem={selectedCustomer}
					searchPlaceholder={c.customerNone.value}
					onSelect={opt => { setCustomer(opt.id ? { id: opt.id, name: opt.label } : null); setOpen(false) }}
				/>
			</PopoverContent>
		</Popover>
	)
}

// Subscribes to labelDraft$ directly so keystrokes only re-render this section.
const LabelsField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item, setLabels } = detail
	const c = useIntlayer('work-item').detail
	const labelDraft = useSelector(labelDraft$)
	const inputRef = useRef<HTMLInputElement>(null)

	const addLabel = () => {
		const next = labelDraft.trim()
		if (!next || item?.labels.includes(next)) {
			labelDraft$.set('')
			return
		}
		setLabels([...(item?.labels ?? []), next])
		labelDraft$.set('')
	}

	const removeLabel = (l: string) => setLabels(item?.labels.filter(existing => existing !== l) ?? [])

	return (
		<div
			role='presentation'
			className='group flex items-start gap-2.5 px-1.5 -mx-1.5 min-h-7 py-1 rounded hover:bg-muted/50 transition-colors cursor-text'
			onClick={() => inputRef.current?.focus()}
		>
			<Tag className='size-3.5 shrink-0 text-606060 mt-0.5' />
			<div className='flex items-center gap-1 flex-wrap flex-1'>
				{item?.labels.map(l => (
					<Badge key={l} variant='tag' className='flex items-center gap-0.5 pr-0.5'>
						{l}
						<button
							type='button'
							aria-label={c.removeLabel.value}
							onClick={e => {
								e.stopPropagation()
								removeLabel(l)
							}}
							className='text-606060 hover:text-foreground p-0.5'
						>
							<X className='size-2.5' />
						</button>
					</Badge>
				))}
				<input
					ref={inputRef}
					value={labelDraft}
					onChange={e => labelDraft$.set(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault()
							addLabel()
						}
					}}
					onBlur={addLabel}
					placeholder={item?.labels.length ? '' : c.addLabelPlaceholder.value}
					className='flex-1 min-w-[80px] text-[13px] bg-transparent outline-none placeholder:text-606060/70 py-0.5'
				/>
			</div>
		</div>
	)
}

const ExternalIdField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item, setExternalId } = detail
	const c = useIntlayer('work-item').detail
	const [draft, setDraft] = useState<string | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	if (!item) return null

	const value = draft ?? item.externalId ?? ''

	const commit = () => {
		if (draft !== null) {
			setExternalId(draft)
			setDraft(null)
		}
	}

	return (
		<div className={rowClass} role='presentation' onClick={() => inputRef.current?.focus()}>
			<Hash className={iconClass} />
			<input
				ref={inputRef}
				value={value}
				onChange={e => setDraft(e.target.value)}
				onBlur={commit}
				onKeyDown={e => {
					if (e.key === 'Enter') e.currentTarget.blur()
					if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur() }
				}}
				placeholder={c.fieldExternalId.value}
				className='flex-1 min-w-0 text-[13px] bg-transparent outline-none placeholder:text-606060/70 truncate'
			/>
		</div>
	)
}

const ReassignField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item } = detail
	const c = useIntlayer('work-item').edit
	const { workflows: allWorkflows } = useAllWorkflows()
	const { reassign } = useReassignWorkflow()
	const [workflowId, setWorkflowId] = useState('')
	const [phaseId, setPhaseId] = useState('')
	const [busy, setBusy] = useState(false)

	if (!item || item.parentId) return null

	const targetWorkflow = allWorkflows.find(w => w.id === workflowId)
	const phases = targetWorkflow ? [...targetWorkflow.phases].sort((a, b) => a.order - b.order) : []
	const phase = phases.find(p => p.id === phaseId)
	const inboxStep = phase?.teamWorkflow.steps.find(s => s.isInbox) ?? phase?.teamWorkflow.steps[0]
	const canReassign = Boolean(targetWorkflow && phase && inboxStep)

	const doReassign = async () => {
		if (!item || !targetWorkflow || !phase || !inboxStep) return
		setBusy(true)
		await reassign({ id: item.id, companyWorkflowId: targetWorkflow.id, phaseId: phase.id, firstStepId: inboxStep.id })
		setBusy(false)
	}

	return (
		<>
			<Separator className='my-2' />
			<div className='flex flex-col gap-2 px-1.5'>
				<div className='flex items-center gap-2 text-[12px] text-606060'>
					<ArrowsLeftRight className='size-3.5 shrink-0' />
					<span>{c.reassignLabel}</span>
				</div>
				<Select value={workflowId} onValueChange={v => { setWorkflowId(v); setPhaseId('') }}>
					<SelectTrigger className='h-7 text-[12px]'>
						<SelectValue placeholder={c.reassignWorkflowPlaceholder.value} />
					</SelectTrigger>
					<SelectContent>
						{allWorkflows.filter(w => w.id !== item.companyWorkflowId).map(w => (
							<SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
						))}
					</SelectContent>
				</Select>
				{targetWorkflow ? (
					<Select value={phaseId} onValueChange={setPhaseId}>
						<SelectTrigger className='h-7 text-[12px]'>
							<SelectValue placeholder={c.reassignPhasePlaceholder.value} />
						</SelectTrigger>
						<SelectContent>
							{phases.map(p => (
								<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : null}
				<Button
					type='button'
					variant='outline'
					size='sm'
					className='self-start h-7 text-[12px]'
					disabled={!canReassign || busy}
					onClick={doReassign}
				>
					{c.reassignButton}
				</Button>
			</div>
		</>
	)
}

const WorkItemSidebar = () => {
	const { detail } = useWorkItemDetailContext()
	const { item } = detail
	const c = useIntlayer('work-item').detail
	const labels = statusLabels(c)

	if (!item) return null

	const statusValue =
		item.status === 'open'
			? `${labels.open} · ${item.workState === 'in_progress' ? c.workStateInProgress.value : c.workStateTodo.value}`
			: labels[item.status]

	return (
		<aside className='w-full lg:w-52 shrink-0 lg:border-l lg:border-border lg:pl-5 flex flex-col gap-0.5'>
			<StatusField item={item} statusValue={statusValue} />
			<PriorityField />
			<SizeField />
			<DueDateField />
			<AssigneeField />
			<CustomerField />
			<ExternalIdField />
			<LabelsField />
			<ReassignField />
		</aside>
	)
}

export { WorkItemSidebar }
